import React, { useEffect, useMemo, useState } from "react";

/* ================= TYPES ================= */

interface OrderItem {
  product: {
    name: string;
    image?: string;
  };
  quantity: number;
  price: number;
}

interface Order {
  id: number;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  total_amount: number;
  order_status: string;
  ordered_at: string;
  items: OrderItem[];
}

const STATUS_OPTIONS = [
  "Pending",
  "Processing",
  "Shipped",
  "Delivered",
  "Cancelled",
];

/* ================= STATUS BADGE ================= */

const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, string> = {
    Pending: "bg-yellow-100 text-yellow-800",
    Processing: "bg-blue-100 text-blue-800",
    Shipped: "bg-purple-100 text-purple-800",
    Delivered: "bg-green-100 text-green-800",
    Cancelled: "bg-red-100 text-red-800",
  };

  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${map[status]}`}>
      {status}
    </span>
  );
};

/* ================= COMPONENT ================= */

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BASE_URL;

  /* ========== LOAD ORDERS ========== */

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/orders`, {
          credentials: "include",
        });
        const data = await res.json();
        setOrders(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API_URL]);

  /* ========== SEARCH FILTER ========== */

  const filteredOrders = useMemo(() => {
    const q = search.toLowerCase();
    return orders.filter(o =>
      o.customer_name.toLowerCase().includes(q) ||
      o.customer_email.toLowerCase().includes(q) ||
      o.customer_phone?.toLowerCase().includes(q) ||
      o.order_status.toLowerCase().includes(q) ||
      o.id.toString().includes(q) ||
      o.items.some(i =>
        i.product.name.toLowerCase().includes(q)
      )
    );
  }, [search, orders]);

  /* ========== STATUS UPDATE ========== */

  const updateStatus = async (id: number, status: string) => {
    if (!window.confirm("Change order status?")) return;

    await fetch(`${API_URL}/orders/${id}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });

    setOrders(prev =>
      prev.map(o => (o.id === id ? { ...o, order_status: status } : o))
    );
  };

  /* ========== PRINT INVOICE ========== */

  const printInvoice = (order: Order) => {
    const w = window.open("", "_blank");
    if (!w) return;

    const statusSteps = ["Pending", "Processing", "Shipped", "Delivered"];
    const currentStep = statusSteps.indexOf(order.order_status);

    w.document.write(`
      <html>
        <head>
          <title>Invoice #${order.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #333; }
            .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
            .header img { height: 60px; }
            .company-details { text-align: right; font-size: 12px; line-height: 1.4; }
            h2 { margin: 0; font-size: 20px; }
            .customer-info { margin-top: 16px; font-size: 14px; }
            .order-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            .order-table th, .order-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .order-table th { background-color: #f5ebe0; }
            .total { font-weight: bold; margin-top: 12px; text-align: right; font-size: 16px; }
            .timeline { display: flex; justify-content: space-between; margin: 24px 0; }
            .step { text-align: center; flex: 1; position: relative; }
            .step:before { content: ''; position: absolute; top: 12px; left: 50%; height: 4px; width: 100%; background: #ddd; z-index: 0; transform: translateX(-50%); }
            .step:first-child:before { left: 50%; width: 50%; }
            .step:last-child:before { width: 50%; }
            .step-circle { width: 24px; height: 24px; border-radius: 50%; background-color: #ddd; margin: 0 auto; z-index: 1; position: relative; }
            .step-active .step-circle { background-color: #f5ebe0; }
            .step-label { margin-top: 8px; font-size: 12px; }
            .footer { margin-top: 32px; font-size: 12px; text-align: center; color: #555; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">
              <img src="https://www.beautonde.com/logo.png" alt="Company Logo" />
            </div>
            <div class="company-details">
              <p>Email: info@beautonde.com</p>
              <p>Phone: +256 701 017 562</p>
              <p>Location: Lweza opposite Shell</p>
              <p>Website: <a href="https://www.beautonde.com">www.beautonde.com</a></p>
            </div>
          </div>

          <h2>Invoice #${order.id}</h2>
          <div class="customer-info">
            <p><strong>Customer Name:</strong> ${order.customer_name}</p>
            <p><strong>Email:</strong> ${order.customer_email}</p>
            <p><strong>Phone:</strong> ${order.customer_phone || "-"}</p>
            <p><strong>Date:</strong> ${new Date(order.ordered_at).toLocaleDateString()}</p>
          </div>

          <div class="timeline">
            ${statusSteps
              .map(
                (s, idx) => `
              <div class="step ${idx <= currentStep ? "step-active" : ""}">
                <div class="step-circle"></div>
                <div class="step-label">${s}</div>
              </div>`
              )
              .join("")}
          </div>

          <table class="order-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th>Price (UGX)</th>
              </tr>
            </thead>
            <tbody>
              ${order.items
                .map(
                  i => `
                <tr>
                  <td>${i.product.name}</td>
                  <td>${i.quantity}</td>
                  <td>${Number(i.price).toLocaleString()}</td>
                </tr>`
                )
                .join("")}
            </tbody>
          </table>

          <p class="total">Total: ${Number(order.total_amount).toLocaleString()} UGX</p>

          <div class="footer">
            <p>Follow us: Facebook | Instagram | Threads | TikTok</p>
            <p>© nzimbaTech 2025</p>
          </div>

          <script>
            window.print();
          </script>
        </body>
      </html>
    `);

    w.document.close();
  };


  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading orders...
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen max-w-7xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4">Orders</h2>

      {/* SEARCH */}
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search orders..."
        className="mb-4 w-full max-w-md rounded-md border px-3 py-2"
      />

      {/* TABLE */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left">ID</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Total</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>

          <tbody>
            {filteredOrders.map(order => (
              <React.Fragment key={order.id}>
                <tr className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">{order.id}</td>
                  <td className="px-4 py-3">{order.customer_name}</td>
                  <td className="px-4 py-3 font-medium">
                    {Number(order.total_amount).toLocaleString()} UGX
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={order.order_status}
                      onChange={e => updateStatus(order.id, e.target.value)}
                      className="border rounded px-2 py-1"
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {new Date(order.ordered_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() =>
                        setExpanded(expanded === order.id ? null : order.id)
                      }
                      className="text-blue-600 text-sm hover:underline"
                    >
                      {expanded === order.id ? "Hide" : "Details"}
                    </button>
                  </td>
                </tr>

                {/* DETAILS + TIMELINE */}
                {expanded === order.id && (
                  <tr className="bg-gray-50">
                    <td colSpan={6} className="px-6 py-4 space-y-4">
                      {/* CUSTOMER */}
                      <div>
                        <h4 className="font-semibold">Customer</h4>
                        <p>{order.customer_name}</p>
                        <p>{order.customer_email}</p>
                        <p>{order.customer_phone || "-"}</p>
                      </div>

                      {/* ITEMS */}
                      <div className="space-y-2">
                        {order.items.map((i, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span>{i.product.name} × {i.quantity}</span>
                            <span>{Number(i.price).toLocaleString()} UGX</span>
                          </div>
                        ))}
                      </div>

                      {/* TIMELINE */}
                      <div className="flex gap-3">
                        {STATUS_OPTIONS.map(s => (
                          <StatusBadge
                            key={s}
                            status={s}
                          />
                        ))}
                      </div>

                      {/* ACTIONS */}
                      <div className="flex gap-3">
                        <button
                          onClick={() => printInvoice(order)}
                          className="rounded bg-indigo-600 px-4 py-2 text-white text-sm"
                        >
                          Print Invoice
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminOrders;
